import {
    FormControlProvider,
    FormStepComponentProps,
    QuestionnaireFormData,
    SIMDocumentTypeAttachmentOption,
} from 'common';
import { SupplierPendingSimRequestFlags } from './useSupplierPendingSimRequests';
import { FC } from 'react';
import {
    CertificateOfInsuranceStep,
    PostSignupFormData,
    PostSignupFormSchema,
    RemittanceStep,
    TaxDetailsStep,
} from './steps';
import { QuestionnaireSimStep } from './steps/QuestionnaireSim';
import { ReviewAndSubmitStep, ReviewRowLabel, SubmissionDocKey, SubmissionState } from './steps/ReviewAndSubmit';
import { SimAttachmentOptions } from './simAttachmentOptions';
import { PaymentFieldConfiguration } from './steps/Remittance/Remittance.utils';
import { getLockedFormStatus } from './steps/LockedFormNotice';
import { PostSignupAgreement } from './usePostSignupAgreements';

type StepComponent = FC<FormStepComponentProps<typeof PostSignupFormSchema>>;

export interface PostSignupStep {
    id: number;
    title: string;
    component: StepComponent;
    fields: (keyof PostSignupFormData)[];
}

export interface BuildPostSignupPipelineOptions {
    buyerQuestionnaires: QuestionnaireFormData[];
    isLoadingQuestionnaires: boolean;
    attachmentOptions: SimAttachmentOptions;
    questionnaireAttachmentOptions: Record<number, SIMDocumentTypeAttachmentOption>;
    attachments: Record<SubmissionDocKey, File[]>;
    attachmentErrors: Record<SubmissionDocKey, boolean>;
    onAttachmentsChange: (key: SubmissionDocKey, files: File[]) => void;
    reviewRows: ReviewRowLabel[];
    submissionStates: Record<SubmissionDocKey, SubmissionState>;
    onRetry: (key: SubmissionDocKey, formData: PostSignupFormData) => void;
    onSkip: (key: SubmissionDocKey) => void;
    paymentFieldConfiguration: PaymentFieldConfiguration | null;
    isSaving: boolean;
    stepAgreements: Partial<Record<keyof PostSignupFormData, PostSignupAgreement>>;
    acceptedAgreements: Partial<Record<keyof PostSignupFormData, boolean>>;
    onAgreementAcceptedChange: (stepKey: keyof PostSignupFormData, accepted: boolean) => void;
}

function createTaxDetailsStepComponent({
    attachmentOptions,
    attachments,
    attachmentErrors,
    onAttachmentsChange,
    submissionStates,
    stepAgreements,
    acceptedAgreements,
    onAgreementAcceptedChange,
}: BuildPostSignupPipelineOptions): StepComponent {
    return function TaxDetailsStepWrapper(props) {
        const handleFilesChange = (files: File[]) => onAttachmentsChange('taxDetails', files);
        const handleAgreementAcceptedChange = (accepted: boolean) => onAgreementAcceptedChange('taxDetails', accepted);
        const lockedStatus = getLockedFormStatus(submissionStates.taxDetails);
        return (
            <FormControlProvider value={{ disabled: lockedStatus != null }}>
                <TaxDetailsStep
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                    attachmentOption={attachmentOptions.taxDetails}
                    files={attachments.taxDetails ?? []}
                    hasError={attachmentErrors.taxDetails ?? false}
                    onFilesChange={handleFilesChange}
                    agreement={stepAgreements.taxDetails}
                    agreementAccepted={acceptedAgreements.taxDetails ?? false}
                    onAgreementAcceptedChange={handleAgreementAcceptedChange}
                    lockedStatus={lockedStatus}
                />
            </FormControlProvider>
        );
    };
}

function createRemittanceStepComponent(
    {
        attachmentOptions,
        attachments,
        attachmentErrors,
        onAttachmentsChange,
        paymentFieldConfiguration,
        submissionStates,
        stepAgreements,
        acceptedAgreements,
        onAgreementAcceptedChange,
    }: BuildPostSignupPipelineOptions,
    taxDetailsScheduled: boolean
): StepComponent {
    return function RemittanceStepWrapper(props) {
        const handleFilesChange = (files: File[]) => onAttachmentsChange('remittance', files);
        const handleAgreementAcceptedChange = (accepted: boolean) => onAgreementAcceptedChange('remittance', accepted);
        const lockedStatus = getLockedFormStatus(submissionStates.remittance);
        return (
            <FormControlProvider value={{ disabled: lockedStatus != null }}>
                <RemittanceStep
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                    attachmentOption={attachmentOptions.remittance}
                    files={attachments.remittance ?? []}
                    hasError={attachmentErrors.remittance ?? false}
                    onFilesChange={handleFilesChange}
                    paymentFieldConfiguration={paymentFieldConfiguration}
                    taxDetailsScheduled={taxDetailsScheduled}
                    agreement={stepAgreements.remittance}
                    agreementAccepted={acceptedAgreements.remittance ?? false}
                    onAgreementAcceptedChange={handleAgreementAcceptedChange}
                    lockedStatus={lockedStatus}
                />
            </FormControlProvider>
        );
    };
}

function createCertificateOfInsuranceStepComponent({
    attachmentOptions,
    attachments,
    attachmentErrors,
    onAttachmentsChange,
    submissionStates,
}: BuildPostSignupPipelineOptions): StepComponent {
    return function CertificateOfInsuranceStepWrapper(props) {
        const handleFilesChange = (files: File[]) => onAttachmentsChange('certificateOfInsurance', files);
        const lockedStatus = getLockedFormStatus(submissionStates.certificateOfInsurance);
        return (
            <FormControlProvider value={{ disabled: lockedStatus != null }}>
                <CertificateOfInsuranceStep
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                    attachmentOption={attachmentOptions.certificateOfInsurance}
                    files={attachments.certificateOfInsurance ?? []}
                    hasError={attachmentErrors.certificateOfInsurance ?? false}
                    onFilesChange={handleFilesChange}
                    lockedStatus={lockedStatus}
                />
            </FormControlProvider>
        );
    };
}

function createQuestionnaireSimStepComponent({
    buyerQuestionnaires,
    isLoadingQuestionnaires,
    questionnaireAttachmentOptions,
    attachments,
    attachmentErrors,
    onAttachmentsChange,
    submissionStates,
}: BuildPostSignupPipelineOptions): StepComponent {
    return function QuestionnaireSimStepWrapper(props) {
        return (
            <QuestionnaireSimStep
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                questionnaires={buyerQuestionnaires}
                isLoadingQuestionnaires={isLoadingQuestionnaires}
                attachmentOptionsBySubTypeId={questionnaireAttachmentOptions}
                attachments={attachments}
                attachmentErrors={attachmentErrors}
                onAttachmentsChange={onAttachmentsChange}
                submissionStates={submissionStates}
            />
        );
    };
}

function createReviewAndSubmitStepComponent({
    reviewRows,
    submissionStates,
    onRetry,
    onSkip,
    buyerQuestionnaires,
    isSaving,
}: BuildPostSignupPipelineOptions): StepComponent {
    return function ReviewAndSubmitStepWrapper() {
        return (
            <ReviewAndSubmitStep
                rows={reviewRows}
                states={submissionStates}
                onRetry={onRetry}
                onSkip={onSkip}
                questionnaires={buyerQuestionnaires}
                actionsDisabled={isSaving}
            />
        );
    };
}

/**
 * Builds the ordered list of post-signup steps based on which supplemental
 * SIM requests the buyer has pending. Always ends with the Review & Submit
 * step when at least one document is requested; if no documents are
 * requested, returns empty and the orchestrator skips straight to the guide.
 */
export function buildPostSignupPipeline(
    flags: SupplierPendingSimRequestFlags,
    options: BuildPostSignupPipelineOptions
): PostSignupStep[] {
    const steps: PostSignupStep[] = [];
    let id = 1;

    if (options.buyerQuestionnaires.length > 0) {
        steps.push({
            id: id++,
            title: 'Questionnaires',
            component: createQuestionnaireSimStepComponent(options),
            fields: ['questionnaires'],
        });
    }
    if (flags.taxDetails) {
        steps.push({
            id: id++,
            title: 'Tax Details',
            component: createTaxDetailsStepComponent(options),
            fields: ['taxDetails'],
        });
    }
    if (flags.remittance) {
        steps.push({
            id: id++,
            title: 'Remittance',
            component: createRemittanceStepComponent(options, flags.taxDetails),
            fields: ['remittance'],
        });
    }
    if (flags.certificateOfInsurance) {
        steps.push({
            id: id++,
            title: 'Insurance',
            component: createCertificateOfInsuranceStepComponent(options),
            fields: ['certificateOfInsurance'],
        });
    }

    if (steps.length > 0) {
        steps.push({
            id: id++,
            title: 'Submit',
            component: createReviewAndSubmitStepComponent(options),
            fields: [],
        });
    }

    return steps;
}
